import { recordDate } from './shipping-data';

export const PRODUCTION_METRICS = {
  ValorTotalRealizado: { label: 'Valor total realizado', unit: 'R$', decimals: 2 },
  qtdeProduzida: { label: 'Quantidade produzida', unit: 'quantidade', decimals: 4 },
  qtdeTotalProduzida: { label: 'Quantidade total produzida', unit: 'quantidade', decimals: 4 },
  m3Produzido: { label: 'm³ produzido', unit: 'm³', decimals: 4 },
  volumeRealizado: { label: 'Volume realizado', unit: 'volume', decimals: 4 },
};

export function formatProduction(value, metric) {
  if (value == null) return 'Não informado';
  if (metric === 'ValorTotalRealizado') return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}${metric === 'm3Produzido' ? ' m³' : ''}`;
}

export function normalizeProduction(row) {
  const result = { ...recordDate(row) };
  for (const field of ['familia', 'classificacao', 'fabricante', 'maquina'])
    result[field] = String(row[field] ?? '').trim() || 'Não informado';
  for (const key of Object.keys(PRODUCTION_METRICS)) {
    const value = row[key];
    const numeric = value == null || value === '' ? null : Number(typeof value === 'string' && value.includes(',')
      ? value.replace(/\./g, '').replace(',', '.') : value);
    if (numeric !== null && !Number.isFinite(numeric)) throw new Error(`Valor inválido na produção: ${PRODUCTION_METRICS[key].label}.`);
    result[key] = numeric;
  }
  return result;
}

export function filterProduction(rows, { year, month, family = '', manufacturer = '', machine = '' }) {
  const matches = (value, selected) => Array.isArray(selected) ? selected.length === 0 || selected.includes(value) : !selected || value === selected;
  return rows.filter(row => (year == null || (row.year === Number(year) && row.month === Number(month)))
    && matches(row.familia, family) && matches(row.fabricante, manufacturer) && matches(row.maquina, machine));
}

function total(rows, metric) {
  if (rows.some(row => row[metric] == null)) return null;
  const value = rows.reduce((sum, row) => sum + row[metric], 0);
  return metric === 'ValorTotalRealizado' ? Math.round(value * 100) / 100 : value;
}

function dayValue(rows, date, metric, today) {
  const matches = rows.filter(row => row.year === date.getFullYear() && row.month === date.getMonth() + 1 && row.day === date.getDate());
  return date > new Date(today.getFullYear(), today.getMonth(), today.getDate()) && matches.length === 0 ? null : total(matches, metric);
}

export function dailyProduction(rows, year, month, metric, today = new Date()) {
  return Array.from({ length: new Date(year, month, 0).getDate() }, (_, index) => ({
    label: String(index + 1).padStart(2, '0'), value: dayValue(rows, new Date(year, month - 1, index + 1), metric, today),
  }));
}

export function weeklyProduction(rows, metric, today = new Date()) {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
  const format = date => `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  return ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((label, index) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    const previous = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index - 7);
    return { label, date: format(date), previousDate: format(previous), value: dayValue(rows, date, metric, today),
      previous: dayValue(rows, previous, metric, today) };
  });
}

export function groupProduction(rows, dimension, metric) {
  const groups = new Map();
  rows.forEach(row => {
    const name = row[dimension];
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(row);
  });
  return [...groups].map(([name, records]) => ({ name, value: total(records, metric),
    ...Object.fromEntries(Object.keys(PRODUCTION_METRICS).map(key => [key, total(records, key)])) }))
    .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity) || a.name.localeCompare(b.name, 'pt-BR'));
}
